import type { CmmdRuntimeDiagnosticsSnapshot } from "@cmmd-forge/contracts";

import {
  DiagnosticsCard,
  DiagnosticsMetrics,
  DiagnosticsSection,
  StatusPill,
  formatTimestamp,
  type DiagnosticsMetric,
} from "./DiagnosticsPanelPrimitives";

function formatState(value: string | null): string {
  return value?.replaceAll("_", " ") ?? "Not reported";
}

function shortCommit(value: string | null): string {
  return value?.slice(0, 10) ?? "Not reported";
}

/**
 * The snapshot fields worth reading, and only those.
 *
 * The full snapshot has twenty, and rendering all twenty made a wall in which
 * a stale lifecycle and a drifted commit looked exactly like the sixteen
 * fields that are always the same. These are the ones that change when
 * something is wrong: what state the runtime is in, whether its Forge build
 * matches what CMMD wants, whether its credentials are current, and — always
 * shown, because "no failure" is itself the answer — how it last failed.
 *
 * `credentialEpoch` and `attestedCredentialEpoch` are one row, not two: the
 * question is whether they agree, and two adjacent numbers made the reader
 * compute that themselves.
 */
function canonicalSnapshotMetrics(
  snapshot: CmmdRuntimeDiagnosticsSnapshot,
): ReadonlyArray<DiagnosticsMetric> {
  const epoch = snapshot.authority.credentialEpoch;
  const attested = snapshot.authority.attestedCredentialEpoch;
  const forgeDrifted =
    snapshot.forge.installedCommit !== null &&
    snapshot.forge.desiredCommit !== null &&
    snapshot.forge.installedCommit !== snapshot.forge.desiredCommit;

  return [
    { label: "Lifecycle", value: formatState(snapshot.lifecycle.state) },
    { label: "Supervised service", value: formatState(snapshot.service.state) },
    { label: "Forge state", value: formatState(snapshot.forge.state) },
    {
      label: forgeDrifted ? "Forge build (drifted)" : "Forge build",
      value: forgeDrifted
        ? `${shortCommit(snapshot.forge.installedCommit)} → ${shortCommit(snapshot.forge.desiredCommit)}`
        : shortCommit(snapshot.forge.installedCommit),
      mono: true,
    },
    {
      label: epoch === attested ? "Credential epoch" : "Credential epoch (unattested)",
      value: epoch === attested ? (epoch?.toString() ?? "Not reported") : `${epoch} → ${attested}`,
    },
    { label: "Last failure", value: formatState(snapshot.failure.classification) },
    { label: "Replication", value: formatState(snapshot.replication.state) },
    { label: "Observed", value: formatTimestamp(snapshot.availability.observedAt) },
  ];
}

export function DiagnosticsCanonicalRuntimeSnapshot({
  snapshot,
  error,
  pending,
}: {
  readonly snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
  readonly error: string | undefined;
  readonly pending: boolean;
}) {
  if (pending) {
    return (
      <DiagnosticsSection
        title="Control-plane observation"
        description="Reading the CMMD-attested snapshot…"
      />
    );
  }
  if (!snapshot) {
    return (
      <DiagnosticsSection
        title="Control-plane observation"
        description={error ?? "CMMD has not returned an authorized snapshot for this target."}
      />
    );
  }
  return (
    <DiagnosticsSection
      title="Control-plane observation"
      id="diagnostics-control-plane"
      description="What CMMD last attested about this target, from forge-context."
      action={
        <StatusPill
          label={snapshot.availability.state.replaceAll("_", " ")}
          tone={snapshot.availability.state === "available" ? "ready" : "muted"}
        />
      }
    >
      <DiagnosticsCard>
        <DiagnosticsMetrics metrics={canonicalSnapshotMetrics(snapshot)} />
      </DiagnosticsCard>
    </DiagnosticsSection>
  );
}
